import { allPhonePrefixSelectOptions, countrySelectOptions, useRouter } from '@application/utils';
import type { CountryCode } from '@gql/graphql';
import {
  Button,
  Card,
  H1SectionTitle,
  InputCheckboxField,
  InputField,
  InputLabel,
  InputSelectField,
  InputWrapper,
  Section,
  SectionSubtitle,
} from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import { Form, useFormikContext } from 'formik';
import type { FC } from 'react';
import type { JoinBreederPageFormValues, JoinBreederPageProps } from '..';
import * as Styled from './styled';

export * from './types';

export const JoinBreederPage: FC<JoinBreederPageProps> = (props) => {
  const { loginLink, onSubmitClick, loading = false } = props;
  const { translate } = useTranslation();
  const { routeLocationDescriptor } = useRouter();
  const countryOptions = countrySelectOptions(translate);
  const phonePrefixes = allPhonePrefixSelectOptions(translate);

  const { isValid } = useFormikContext<JoinBreederPageFormValues>();
  return (
    <Styled.Wrapper>
      <Card size="small">
        <H1SectionTitle>{translate('signup.breeder.title')}</H1SectionTitle>
        <SectionSubtitle>{translate('signup.quote')}</SectionSubtitle>
        <Section>
          <Form>
            <InputWrapper>
              <InputLabel required>{translate('common.inputs.firstName.label')}</InputLabel>
              <InputField name="firstName" />
            </InputWrapper>

            <InputWrapper>
              <InputLabel required>{translate('common.inputs.lastName.label')}</InputLabel>
              <InputField name="lastName" />
            </InputWrapper>

            <InputWrapper>
              <InputLabel required>{translate('common.inputs.email.label')}</InputLabel>
              <InputField name="email" type="email" />
            </InputWrapper>

            <InputWrapper>
              <InputLabel required>{translate('common.inputs.phone.label')}</InputLabel>

              <InputWrapper inline width="39%" fullWidthBreakpoint="min" noMargin>
                <InputSelectField name="phonePrefixCountry" options={phonePrefixes} />
              </InputWrapper>

              <InputWrapper inline width="61%" fullWidthBreakpoint="min" last noMargin>
                <InputField name="phoneNumber" type="tel" />
              </InputWrapper>
            </InputWrapper>

            <InputWrapper>
              <InputLabel required>{translate('common.inputs.country.label')}</InputLabel>
              <InputSelectField<CountryCode> name="country" options={countryOptions} />
            </InputWrapper>

            <InputWrapper>
              <InputLabel required>{translate('common.inputs.password.label')}</InputLabel>
              <InputField name="password" type="password" />
            </InputWrapper>

            <InputWrapper>
              <InputLabel required>{translate('common.inputs.passwordRepeat.label')}</InputLabel>
              <InputField name="confirmPassword" type="password" />
            </InputWrapper>

            <InputWrapper>
              <InputCheckboxField name="acceptedChart">
                <Styled.LinkSpan
                  dangerouslySetInnerHTML={{
                    __html: translate('signup.breederChart').replace(
                      '$href',
                      routeLocationDescriptor('chart').pathname ?? '',
                    ),
                  }}
                />
              </InputCheckboxField>
            </InputWrapper>

            <InputWrapper>
              <InputCheckboxField name="acceptedTerms">
                <Styled.LinkSpan
                  dangerouslySetInnerHTML={{
                    __html: translate('signup.termsPolicy').replace(
                      '$href',
                      routeLocationDescriptor('terms').pathname ?? '',
                    ),
                  }}
                />
              </InputCheckboxField>
            </InputWrapper>

            <InputWrapper>
              <Button
                color="blue"
                onClick={onSubmitClick}
                loading={loading}
                disabled={loading || !isValid}
              >
                {translate('common.buttons.register')}
              </Button>
            </InputWrapper>

            <InputWrapper>
              <Styled.LoginWrapper>
                <span>{translate('signup.alreadyHasAccount')}</span>
                <Styled.LoginLink to={loginLink}>
                  {translate('common.buttons.login')}
                </Styled.LoginLink>
              </Styled.LoginWrapper>
            </InputWrapper>
          </Form>
        </Section>
      </Card>
    </Styled.Wrapper>
  );
};
